/**
 * Map Legend Component - Shows status colors and progress indicators
 */
import { useTranslation } from "react-i18next";
import { ADDRESS_STATUS_COLORS } from "../layers";
import "./MapLegend.css";

export interface MapLegendProps {
    showAddresses?: boolean;
    showAssignments?: boolean;
    showRoads?: boolean;
}

export const MapLegend = ({
    showAddresses = true,
    showAssignments = true,
    showRoads = false
}: MapLegendProps): JSX.Element => {
    const { t } = useTranslation();

    return (
        <div className="map-legend" role="region" aria-label={t("map.legend.title")}>
            <h3 className="map-legend__title">{t("map.legend.title")}</h3>

            {showAddresses && (
                <div className="map-legend__section">
                    <h4 className="map-legend__section-title">
                        {t("map.legend.addresses")}
                    </h4>
                    <ul className="map-legend__list">
                        <li className="map-legend__item">
                            <span
                                className="map-legend__color"
                                style={{ backgroundColor: ADDRESS_STATUS_COLORS.delivered }}
                                aria-hidden="true"
                            />
                            <span>{t("map.legend.addresses.delivered")}</span>
                        </li>
                        <li className="map-legend__item">
                            <span
                                className="map-legend__color"
                                style={{ backgroundColor: ADDRESS_STATUS_COLORS.partial }}
                                aria-hidden="true"
                            />
                            <span>{t("map.legend.addresses.partial")}</span>
                        </li>
                        <li className="map-legend__item">
                            <span
                                className="map-legend__color"
                                style={{ backgroundColor: ADDRESS_STATUS_COLORS.pending }}
                                aria-hidden="true"
                            />
                            <span>{t("map.legend.addresses.pending")}</span>
                        </li>
                        <li className="map-legend__item">
                            <span
                                className="map-legend__color"
                                style={{ backgroundColor: ADDRESS_STATUS_COLORS.blocked }}
                                aria-hidden="true"
                            />
                            <span>{t("map.legend.addresses.blocked")}</span>
                        </li>
                        <li className="map-legend__item">
                            <span
                                className="map-legend__color"
                                style={{ backgroundColor: ADDRESS_STATUS_COLORS.hidden }}
                                aria-hidden="true"
                            />
                            <span>{t("map.legend.addresses.hidden")}</span>
                        </li>
                    </ul>
                </div>
            )}

            {showAssignments && (
                <div className="map-legend__section">
                    <h4 className="map-legend__section-title">
                        {t("map.legend.assignments")}
                    </h4>
                    <ul className="map-legend__list">
                        <li className="map-legend__item">
                            <span
                                className="map-legend__color map-legend__color--gradient"
                                style={{
                                    background:
                                        "linear-gradient(to right, #f97316 0%, #facc15 50%, #22c55e 100%)"
                                }}
                                aria-hidden="true"
                            />
                            <span>{t("map.legend.assignments.progress")}</span>
                        </li>
                        <li className="map-legend__item">
                            <span
                                className="map-legend__color"
                                style={{ backgroundColor: "#3b82f6" }}
                                aria-hidden="true"
                            />
                            <span>{t("map.legend.assignments.planned")}</span>
                        </li>
                        <li className="map-legend__item">
                            <span
                                className="map-legend__color"
                                style={{ backgroundColor: "#f59e0b" }}
                                aria-hidden="true"
                            />
                            <span>{t("map.legend.assignments.inProgress")}</span>
                        </li>
                        <li className="map-legend__item">
                            <span
                                className="map-legend__color"
                                style={{ backgroundColor: "#22c55e" }}
                                aria-hidden="true"
                            />
                            <span>{t("map.legend.assignments.completed")}</span>
                        </li>
                    </ul>
                </div>
            )}

            {showRoads && (
                <div className="map-legend__section">
                    <h4 className="map-legend__section-title">
                        {t("map.legend.roads")}
                    </h4>
                    <ul className="map-legend__list">
                        <li className="map-legend__item">
                            <span
                                className="map-legend__color map-legend__color--line"
                                style={{ backgroundColor: "#94a3b8" }}
                                aria-hidden="true"
                            />
                            <span>{t("map.legend.roads.default")}</span>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
};